import { Link, useParams } from "react-router-dom";
import { agentById, referenceQuotesByAgent } from "@/data";
import { dataset } from "@/data";
import { getScore } from "@/data/scores";
import { RadarChart } from "@/components/charts";
import { CellValue, ConfBadge, ScoreBar, SourceRef, StatusDot, Coverage } from "@/components/ui";
import { pct, scoreColor } from "@/format";
import type { usePins } from "@/hooks";

export default function AgentPage({ pins }: { pins: ReturnType<typeof usePins> }) {
  const { id } = useParams();
  const agent = id ? agentById.get(id) : undefined;
  if (!agent) return <div className="empty">Agent not found. <Link to="/">Back to list</Link></div>;
  const score = getScore(agent.id)!;

  const radar = {
    axes: dataset.scoring.categories.map((c) => c.label),
    series: [
      {
        label: agent.name,
        color: "#2f6bff",
        values: dataset.scoring.categories.map((c) => score.categories.find((x) => x.id === c.id)?.score ?? null),
      },
    ],
  };

  return (
    <div>
      <div className="row spread wrap">
        <div className="row" style={{ gap: 14 }}>
          <span className="avatar" style={{ width: 52, height: 52, fontSize: "1.1rem" }}>
            {agent.logoText ?? agent.name.slice(0, 2).toUpperCase()}
          </span>
          <div>
            <h1 style={{ marginBottom: 4 }}>
              <span className="row" style={{ gap: 8 }}>
                <StatusDot status={agent.status.v} /> {agent.name}
              </span>
            </h1>
            <div className="row wrap small muted" style={{ gap: 10 }}>
              <span>{agent.aliases.slice(0, 3).join(" · ") || "—"}</span>
              <span className="chip">{agent.verification}</span>
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn sm" onClick={() => pins.toggle(agent.id)}>
            {pins.has(agent.id) ? "★ Pinned" : "☆ Pin to compare"}
          </button>
        </div>
      </div>

      {agent.status.v === "defunct" && (
        <div className="banner mt">This agent is <strong>defunct / no longer operating normally</strong>. Kept for historical reference.</div>
      )}

      <div className="grid mt2 agent-cols">
        {/* Left: score + key facts */}
        <div className="grid" style={{ gap: 14 }}>
          <div className="card card-pad">
            <div className="row spread">
              <h3 style={{ margin: 0 }}>Overall</h3>
              <Coverage value={score.overallCoverage} />
            </div>
            <div className="mt"><ScoreBar score={score.overall} width={160} /></div>
            <RadarChart axes={radar.axes} series={radar.series} size={280} />
            <p className="tiny faint">
              Deterministic score (v{dataset.scoring.version}). Coverage = share of weight backed by
              known data. See <Link to="/methodology">methodology</Link>.
            </p>
          </div>

          <div className="card card-pad">
            <h3>Key facts</h3>
            <dl className="kv">
              <dt>Status</dt><dd><CellValue cell={agent.status} /></dd>
              <dt>Founded</dt><dd><CellValue cell={agent.foundingYear} /></dd>
              <dt>Website</dt><dd><CellValue cell={agent.website} render={(v) => <a href={v} target="_blank" rel="noreferrer noopener">{v.replace(/^https?:\/\//, "")}</a>} /></dd>
              <dt>HQ country</dt><dd><CellValue cell={agent.country} /></dd>
              <dt>Service fee</dt><dd><CellValue cell={agent.serviceFee} render={(v: any) => (v.kind === "none" ? "No per-item fee" : v.kind === "percent" ? `${(v.percent * 100).toFixed(1)}%` : v.kind)} /></dd>
              <dt>Trustpilot</dt><dd><CellValue cell={agent.trustpilot} render={(v: any) => `${v.score}/5 · ${v.reviews} reviews`} /></dd>
              <dt>Payments</dt><dd><CellValue cell={agent.paymentMethods} render={(v) => (v as string[]).join(", ")} /></dd>
              <dt>Support</dt><dd><CellValue cell={agent.supportChannels} render={(v) => (v as string[]).join(", ")} /></dd>
              <dt>Free storage</dt><dd><CellValue cell={agent.freeStorageDays} render={(v) => `${v} days`} /></dd>
            </dl>
            {agent.sharesBackendWith.length > 0 && (
              <p className="small mt">
                <strong>Related / shared backend:</strong>{" "}
                {agent.sharesBackendWith.map((bid, i) => (
                  <span key={bid}>
                    {i > 0 && ", "}
                    <Link to={`/agent/${bid}`}>{agentById.get(bid)?.name ?? bid}</Link>
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>

        {/* Right: breakdown, facts, features, shipping */}
        <div className="grid" style={{ gap: 14 }}>
          {agent.notableFacts && agent.notableFacts.length > 0 && (
            <div className="card card-pad">
              <div className="row spread">
                <h3 style={{ margin: 0 }}>Notable facts</h3>
                <SourceRef ids={["research:roster-2026-07"]} />
              </div>
              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                {agent.notableFacts.map((f, i) => (
                  <li key={i} className="small" style={{ marginBottom: 4 }}>{f}</li>
                ))}
              </ul>
              {agent.redditSentiment.v && (
                <p className="small muted mt">
                  <strong>Community sentiment:</strong> {agent.redditSentiment.v} <ConfBadge conf={agent.redditSentiment.conf} />
                </p>
              )}
            </div>
          )}

          <div className="card card-pad">
            <h3>Score breakdown</h3>
            <p className="tiny faint" style={{ marginTop: -4 }}>
              Each metric: raw input → normalized 0–100 × weight. Unknown metrics are excluded (not
              scored as zero) and lower the category’s coverage.
            </p>
            {score.categories.map((c) => (
              <div key={c.id} className="panel" style={{ padding: "10px 12px", marginBottom: 10 }}>
                <div className="row spread">
                  <strong style={{ textTransform: "capitalize" }}>{c.label}</strong>
                  <span className="row" style={{ gap: 10 }}>
                    <span className="score-num">{c.score === null ? "—" : c.score.toFixed(1)}</span>
                    <Coverage value={c.coverage} />
                  </span>
                </div>
                <div className="mt">
                  {c.metrics.map((m) => {
                    const v = m.normalized === null ? null : m.normalized * 100;
                    return (
                      <div key={m.id} style={{ margin: "9px 0" }}>
                        <div className="row spread wrap" style={{ gap: "2px 10px", alignItems: "baseline" }}>
                          <span className="tiny" style={{ color: "var(--text-dim)", minWidth: 0, overflowWrap: "anywhere" }}>{m.label}</span>
                          <span className="row tiny faint" style={{ gap: 8, flex: "none", whiteSpace: "nowrap" }}>
                            <span className="score-num" style={{ color: "var(--text)" }}>{v === null ? "—" : v.toFixed(0)}</span>
                            <span>w&nbsp;{pct(m.weight)}</span>
                            {v === null ? <span>unknown</span> : <ConfBadge conf={m.conf} />}
                            <SourceRef ids={m.src} />
                          </span>
                        </div>
                        <span className="scorebar" style={{ display: "block", height: 6, marginTop: 4 }}>
                          <span style={{ width: `${v ?? 0}%`, background: scoreColor(v) }} />
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="card card-pad">
            <h3>Features</h3>
            <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 6 }}>
              {dataset.features.map((f) => {
                const c = agent.features[f.id];
                const state = !c || c.v === null ? "?" : c.v ? "✓" : "✗";
                const color = state === "✓" ? "var(--good)" : state === "✗" ? "var(--bad)" : "var(--text-faint)";
                return (
                  <div key={f.id} className="row" style={{ gap: 8 }}>
                    <span style={{ color, fontWeight: 700, width: 14 }}>{state}</span>
                    <span className="small" title={f.description}>{f.label}</span>
                    {c && c.v !== null && <SourceRef ids={c.src} />}
                  </div>
                );
              })}
            </div>
          </div>

          {(() => {
            const quotes = (referenceQuotesByAgent.get(agent.id) ?? [])
              .filter((q) => q.parcelSpec === "owncalc-5kg" && !q.modeled && q.priceCny != null);
            const spec = dataset.referenceQuotes.parcelSpecs["owncalc-5kg"];
            if (quotes.length === 0) {
              return (
                <div className="card card-pad">
                  <h3>Live shipping rates</h3>
                  <p className="faint small">
                    No live capture for this agent yet — see <Link to="/coverage">Data coverage</Link> for why
                    (its own calculator may be login-gated, dead, or a duplicate).
                  </p>
                </div>
              );
            }
            const dests = new Set(quotes.map((q) => q.countryCode));
            const sorted = [...quotes].sort(
              (a, b) => a.countryCode.localeCompare(b.countryCode) || a.priceCny! - b.priceCny!
            );
            return (
              <div className="card card-pad">
                <div className="row spread wrap">
                  <h3 style={{ margin: 0 }}>Live shipping rates <span className="badge high">100% real</span></h3>
                  <SourceRef ids={quotes[0].src} />
                </div>
                <p className="tiny faint" style={{ marginTop: 2 }}>
                  Captured live from <strong>{agent.name}’s own calculator</strong> (guest, no login) for a{" "}
                  {spec ? `${(spec.weightG) / 1000} kg, ${spec.lengthCm}×${spec.widthCm}×${spec.heightCm} cm` : "fixed"} parcel —
                  {" "}{quotes.length} lines across {dests.size} destinations. Agent’s own quoted total incl. fees.
                </p>
                <div className="table-wrap" style={{ marginTop: 8, maxHeight: 460 }}>
                  <table className="data">
                    <thead><tr><th>To</th><th>Line</th><th className="num">Price</th><th className="num">ETA</th></tr></thead>
                    <tbody>
                      {sorted.map((q) => (
                        <tr key={q.id}>
                          <td>{q.countryCode}</td>
                          <td>{q.lineName}</td>
                          <td className="num">¥{q.priceCny}{q.priceUsd ? <span className="tiny faint"> ≈${q.priceUsd}</span> : null}</td>
                          <td className="num">{q.etaMinDays === null ? "—" : `${q.etaMinDays}–${q.etaMaxDays} d`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Link to={`/calculator?c=US`} className="btn sm mt">Compare across agents →</Link>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
